// Settings dialog (ui-qol §5): 360 px white card, radius 10, grey × top-right.
// - Region select (only with more than one region): disabled header "— Choose a region —", then
//   "{Region} ({players})".
// - Graphics select: disabled header "— Graphics —", then "Graphics: Sharp" … "Graphics: Auto".
// - A #F5F5F5 panel of round checkboxes, two 120 px columns, in agar's order (PARITY_TOGGLES):
//   Hide skins | Hide names / Plain colors | Dark mode / Show mass | Skip round summary /
//   Game sounds | Menu sounds. Checkboxes are silent (audio §3 row 8).
// - "More options ▸" (open state remembered in settings.moreOpen): the extras (MORE_OPTIONS) —
//   minimap, bigger players on the minimap, FPS & ping, Hold W, volume, reduce motion, arena
//   style, money label unit (USD / SOL), and on touch devices touch steering and "Stop when finger
//   lifts" — plus "Show the controls hint again".
// - Footer: version line and the blue 150 × 35 Spectate button (disabled while alive, tooltip
//   "Finish your round first").
// Every change applies at once through `SettingsStore.set`. Esc closes (deliberate deviation).
//
// The row builders are DOM-free (unit-tested). Consumer: app.ts (gear on the play card).

import {
  ARENA_STEER_MODES,
  type BoolSettingKey,
  LABEL_CURRENCY_SETTINGS,
  MORE_OPTIONS,
  PARITY_TOGGLES,
  QUALITY_SETTINGS,
  type QualitySetting,
  type Settings,
  type SettingsStore,
  TOUCH_STEER_MODES,
} from '../../settings/store.js';
import { RENDER_STYLES } from '@ballio/shared';
import type { UiClickSource } from '../../events/bus.js';
import type { ApiInfoResponse } from '../../net/discovery.js';
import { h, show } from '../dom.js';
import { type I18nKey, t } from '../i18n/t.js';
import { type ModalHandle, openModal } from './modal.js';

/** i18n label key per setting. */
const LABELS: Partial<Record<keyof Settings, I18nKey>> = {
  hideSkins: 'settings.hideSkins',
  hideNames: 'settings.hideNames',
  plainColors: 'settings.plainColors',
  darkMode: 'settings.darkMode',
  showMass: 'settings.showMass',
  skipSummary: 'settings.skipSummary',
  gameSounds: 'settings.gameSounds',
  menuSounds: 'settings.menuSounds',
  showMinimap: 'settings.minimap',
  minimapPlayers: 'settings.minimapPlayers',
  showStats: 'settings.stats',
  holdToEject: 'settings.holdEject',
  volume: 'settings.volume',
  reduceMotion: 'settings.reduceMotion',
  style: 'settings.style',
  labelCurrency: 'settings.labelCurrency',
  touchSteer: 'settings.touchSteer',
  touchStopOnRelease: 'settings.touchStop',
  arenaSteer: 'settings.arenaSteer',
  arenaMinimapAll: 'settings.arenaMinimapAll',
  arenaAlarm: 'settings.arenaAlarm',
};

export function settingLabel(key: keyof Settings): string {
  const k = LABELS[key];
  return k ? t(k) : String(key);
}

export interface ToggleView {
  readonly key: BoolSettingKey;
  readonly label: string;
  readonly checked: boolean;
}

/** The eight parity checkboxes in agar's order (ui-qol §5). */
export function parityToggles(s: Settings): ToggleView[] {
  return PARITY_TOGGLES.map((key) => ({ key, label: settingLabel(key), checked: s[key] }));
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export type MoreRow =
  | {
      readonly kind: 'toggle';
      readonly key: BoolSettingKey;
      readonly label: string;
      readonly checked: boolean;
    }
  | {
      readonly kind: 'range';
      readonly key: 'volume';
      readonly label: string;
      readonly value: number;
    }
  | {
      readonly kind: 'select';
      readonly key: 'reduceMotion' | 'style' | 'labelCurrency' | 'touchSteer' | 'arenaSteer';
      readonly label: string;
      readonly value: string;
      readonly options: readonly SelectOption[];
    };

export interface MoreRowContext {
  /** Touch device: the touch rows are shown. */
  readonly touch: boolean;
  /** WELCOME minimapPlayersAllowed (the row is hidden when the server forbids it). */
  readonly minimapPlayersAllowed: boolean;
}

/** "More options" rows in MORE_OPTIONS order, filtered for the device (DOM-free). */
export function moreRows(s: Settings, ctx: MoreRowContext): MoreRow[] {
  const rows: MoreRow[] = [];
  for (const key of MORE_OPTIONS) {
    const label = settingLabel(key);
    switch (key) {
      case 'volume':
        rows.push({ kind: 'range', key, label, value: Math.round(s.volume * 100) });
        break;
      case 'reduceMotion':
        rows.push({
          kind: 'select',
          key,
          label,
          value: s.reduceMotion === null ? 'system' : s.reduceMotion ? 'on' : 'off',
          options: [
            { value: 'system', label: t('settings.motion.system') },
            { value: 'on', label: t('settings.motion.on') },
            { value: 'off', label: t('settings.motion.off') },
          ],
        });
        break;
      case 'style':
        rows.push({
          kind: 'select',
          key,
          label,
          value: s.style,
          options: RENDER_STYLES.map((v) => ({
            value: v,
            label: t(`settings.style.${v}` as I18nKey),
          })),
        });
        break;
      case 'labelCurrency':
        rows.push({
          kind: 'select',
          key,
          label,
          value: s.labelCurrency,
          options: LABEL_CURRENCY_SETTINGS.map((v) => ({
            value: v,
            label: t(`settings.currency.${v}` as I18nKey),
          })),
        });
        break;
      case 'touchSteer':
        if (!ctx.touch) break;
        rows.push({
          kind: 'select',
          key,
          label,
          value: s.touchSteer,
          options: TOUCH_STEER_MODES.map((v) => ({
            value: v,
            label: t(v === 'joystick' ? 'settings.touchJoystick' : 'settings.touchFollow'),
          })),
        });
        break;
      case 'arenaSteer':
        if (!ctx.touch) break;
        rows.push({
          kind: 'select',
          key,
          label,
          value: s.arenaSteer,
          options: ARENA_STEER_MODES.map((v) => ({
            value: v,
            label: t(v === 'stick' ? 'settings.arenaSteer.stick' : 'settings.arenaSteer.point'),
          })),
        });
        break;
      case 'touchStopOnRelease':
        if (!ctx.touch) break;
        rows.push({ kind: 'toggle', key, label, checked: s[key] });
        break;
      case 'minimapPlayers':
        if (!ctx.minimapPlayersAllowed) break;
        rows.push({ kind: 'toggle', key, label, checked: s[key] });
        break;
      default:
        if (typeof s[key] === 'boolean') {
          rows.push({
            kind: 'toggle',
            key: key as BoolSettingKey,
            label,
            checked: s[key] as boolean,
          });
        }
    }
  }
  return rows;
}

/** Graphics select: "Graphics: Sharp" … "Graphics: Very low", then "Graphics: Auto" (ui-qol §5). */
export function graphicsOptions(): SelectOption[] {
  const order: QualitySetting[] = [...QUALITY_SETTINGS.filter((q) => q !== 'auto'), 'auto'];
  return order.map((q) => ({
    value: q,
    label: t('settings.graphicsItem', { level: t(`settings.quality.${q}` as I18nKey) }),
  }));
}

/** Region select options ("{name} ({players})"); empty with one region (the select is hidden). */
export function regionOptions(info: ApiInfoResponse | null): SelectOption[] {
  if (!info || info.regions.length < 2) return [];
  return info.regions.map((r) => ({
    value: r.id,
    label: t('settings.regionItem', { name: r.name, players: r.players }),
  }));
}

/** The reduce-motion select value → setting. */
export function motionSetting(value: string): boolean | null {
  return value === 'on' ? true : value === 'off' ? false : null;
}

export interface SettingsDeps {
  readonly store: SettingsStore;
  info(): ApiInfoResponse | null;
  /** The region in use (its id), for the select. */
  currentRegion(): string;
  onRegion?(id: string): void;
  /** True when Spectate is allowed (not alive, connected). */
  canSpectate(): boolean;
  onSpectate(): void;
  /** Help ▸ / settings ▸ "Show the controls hint again". */
  onShowHintAgain(): void;
  minimapPlayersAllowed(): boolean;
  playClick?(source: UiClickSource): void;
  readonly touch: boolean;
  readonly version: string;
  onClose?(): void;
}

function selectEl(
  options: readonly SelectOption[],
  value: string,
  header: string | null,
  onChange: (v: string) => void,
  cls: string,
): HTMLSelectElement {
  const sel = h(
    'select',
    { class: cls, onChange: () => onChange(sel.value) },
    header !== null ? h('option', { value: '', disabled: true }, header) : null,
    options.map((o) => h('option', { value: o.value }, o.label)),
  );
  sel.value = value;
  return sel;
}

function checkbox(
  view: { key: string; label: string; checked: boolean },
  onChange: (v: boolean) => void,
) {
  const input = h('input', {
    type: 'checkbox',
    class: 'round-check',
    checked: view.checked,
    onChange: () => onChange(input.checked),
  });
  return h(
    'label',
    { class: 'check-row', dataset: { key: view.key } },
    input,
    h('span', null, view.label),
  );
}

/** Opens the settings dialog. */
export function openSettings(deps: SettingsDeps): ModalHandle {
  const store = deps.store;
  const s = store.get();

  const regions = regionOptions(deps.info());
  const region =
    regions.length > 0
      ? selectEl(
          regions,
          deps.currentRegion(),
          t('settings.regionHeader'),
          (v) => deps.onRegion?.(v),
          'settings-select',
        )
      : null;
  const graphics = selectEl(
    graphicsOptions(),
    s.quality,
    t('settings.graphicsHeader'),
    (v) => store.set('quality', v as QualitySetting),
    'settings-select',
  );

  const panel = h(
    'div',
    { class: 'settings-panel' },
    parityToggles(s).map((v) => checkbox(v, (on) => store.set(v.key, on))),
  );

  const moreBody = h('div', { class: 'settings-more-body' });
  const renderMore = (): void => {
    const cur = store.get();
    moreBody.replaceChildren(
      ...moreRows(cur, {
        touch: deps.touch,
        minimapPlayersAllowed: deps.minimapPlayersAllowed(),
      }).map((row) => {
        switch (row.kind) {
          case 'toggle':
            return checkbox(row, (on) => store.set(row.key, on));
          case 'range': {
            const out = h('span', { class: 'range-value' }, `${row.value}%`);
            const input = h('input', {
              type: 'range',
              min: 0,
              max: 100,
              step: 5,
              value: String(row.value),
              'aria-label': row.label,
              onInput: () => {
                out.textContent = `${input.value}%`;
                store.set('volume', Number(input.value) / 100);
              },
            });
            return h(
              'label',
              { class: 'more-row more-range' },
              h('span', null, row.label),
              input,
              out,
            );
          }
          case 'select':
            return h(
              'label',
              { class: 'more-row more-select' },
              h('span', null, row.label),
              selectEl(
                row.options,
                row.value,
                null,
                (v) => {
                  if (row.key === 'reduceMotion') store.set('reduceMotion', motionSetting(v));
                  else store.update({ [row.key]: v } as Partial<Settings>);
                },
                'more-select-input',
              ),
            );
        }
      }),
      h(
        'button',
        {
          type: 'button',
          class: 'link-btn hint-again',
          onClick: () => {
            deps.playClick?.('help');
            deps.onShowHintAgain();
            hintDone.hidden = false;
          },
        },
        t('help.again'),
      ),
      hintDone,
    );
  };
  const hintDone = h(
    'p',
    { class: 'hint-again-done', role: 'status', hidden: true },
    t('help.againDone'),
  );
  const moreBtn = h(
    'button',
    {
      type: 'button',
      class: 'link-btn settings-more-toggle',
      'aria-expanded': s.moreOpen ? 'true' : 'false',
      onClick: () => store.set('moreOpen', !store.get().moreOpen),
    },
    h('span', null, t('settings.more')),
    h('span', { class: 'more-caret', 'aria-hidden': 'true' }, '▸'),
  );
  const applyMore = (open: boolean): void => {
    moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    moreBtn.classList.toggle('is-open', open);
    show(moreBody, open);
  };
  renderMore();
  applyMore(s.moreOpen);

  const canWatch = deps.canSpectate();
  const spectate = h(
    'button',
    {
      type: 'button',
      class: 'btn-spectate',
      'aria-disabled': canWatch ? 'false' : 'true',
      title: canWatch ? null : t('settings.watchDisabled'),
      onClick: () => {
        if (!deps.canSpectate()) return;
        deps.playClick?.('spectate');
        handle.close();
        deps.onSpectate();
      },
    },
    t('settings.watch'),
  );

  const content = h(
    'div',
    { class: 'settings' },
    h('h2', { class: 'sr-only' }, t('settings.title')),
    region,
    graphics,
    panel,
    moreBtn,
    moreBody,
    h('p', { class: 'settings-version' }, t('settings.version', { version: deps.version })),
    spectate,
  );

  // Keep the controls in sync with changes made elsewhere (e.g. backslash toggles the FPS line).
  const off = store.subscribe((next, prev) => {
    if (next.moreOpen !== prev.moreOpen) applyMore(next.moreOpen);
    for (const input of panel.querySelectorAll<HTMLInputElement>('input[type=checkbox]')) {
      const key = input.closest<HTMLElement>('[data-key]')?.dataset['key'] as
        BoolSettingKey | undefined;
      if (key && input.checked !== next[key]) input.checked = next[key];
    }
    if (graphics.value !== next.quality) graphics.value = next.quality;
    const moreChanged = MORE_OPTIONS.some((k) => k !== 'volume' && next[k] !== prev[k]);
    if (moreChanged) renderMore();
  });

  const handle = openModal(content, {
    label: t('settings.title'),
    className: 'modal-settings',
    onCloseClick: () => deps.playClick?.('close'),
    onClose: () => {
      off();
      deps.onClose?.();
    },
  });
  return handle;
}
